import type { FC, ComponentType, ComponentProps } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import EditIcon from '@atlaskit/icon/core/edit';
import ThumbsUpIcon from '@atlaskit/icon/core/thumbs-up';
import CommentIcon from '@atlaskit/icon/core/comment';
import ClockIcon from '@atlaskit/icon/core/clock';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { NarrowPageCardView } from '@confluence/entity-view/entry-points/NarrowPageCardView.ts';
import { RecentWorkListItemView } from '@confluence/entity-view/entry-points/RecentWorkListItemView.ts';

import type { HomeRecentActivitiesQuery_activities_Activities_myActivities_MyActivities_all_ActivitiesConnection_edges_ActivityEdge_node_ActivitiesItem as RecentActivities } from '../Recent/__types__/HomeRecentActivitiesQuery.ts';
import { transformRecentToPageCard } from '../pageCardTransformers.ts';
import { transformActivityData, ActivityEventType } from '../homeViewsQueryHelpers.ts';

import type { HomeRecentWorkQuery_activities_myActivities_all_edges_node as RecentWork } from './__types__/HomeRecentWorkQuery.ts';

const i18n = defineMessages({
	edited: {
		id: 'home.views.overview.recent-work.action.edited',
		defaultMessage: 'You edited {timeAgo}',
		description:
			"When you last edited the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	visited: {
		id: 'home.views.overview.recent-work.action.visited',
		defaultMessage: 'You visited {timeAgo}',
		description:
			"When you last visited the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	created: {
		id: 'home.views.overview.recent-work.action.created',
		defaultMessage: 'You created {timeAgo}',
		description:
			"When you created the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	liked: {
		id: 'home.views.overview.recent-work.action.liked',
		defaultMessage: 'You liked {timeAgo}',
		description:
			"When you liked the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	commented: {
		id: 'home.views.overview.recent-work.action.commented',
		defaultMessage: 'You commented {timeAgo}',
		description:
			"When you commented on the page. 'timeAgo' is of the form `1 hour ago` or a date `September 15, 2020` (in English)",
	},
	unpublished: {
		id: 'home.views.overview.recent-work.lozenge.unpublished',
		defaultMessage: 'unpublished changes',
		description: 'Label on a page that has changes which have not yet been published.',
	},
	editedShort: {
		id: 'home.views.overview.recent-work.action.edited.short',
		defaultMessage: 'Edited',
		description:
			'When you last edited the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	visitedShort: {
		id: 'home.views.overview.recent-work.action.visited.short',
		defaultMessage: 'Visited',
		description:
			'When you last visited the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	createdShort: {
		id: 'home.views.overview.recent-work.action.created.short',
		defaultMessage: 'Created',
		description:
			'When you created the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	likedShort: {
		id: 'home.views.overview.recent-work.action.liked.short',
		defaultMessage: 'Liked',
		description:
			'When you liked the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
	commentedShort: {
		id: 'home.views.overview.recent-work.action.commented.short',
		defaultMessage: 'Commented',
		description:
			'When you commented on the page. Timestamp is of the form `1 hour ago` or a date `September 15, 2020` (in English)',
	},
});

const ActionIconStrings: Record<
	ActivityEventType,
	[ComponentType<ComponentProps<typeof ClockIcon>>, MessageDescriptor, MessageDescriptor]
> = {
	VIEWED: [ClockIcon, i18n.visited, i18n.visitedShort],
	EDITED: [EditIcon, i18n.edited, i18n.editedShort],
	UPDATED: [EditIcon, i18n.edited, i18n.editedShort],
	CREATED: [EditIcon, i18n.created, i18n.createdShort],
	PUBLISHED: [EditIcon, i18n.edited, i18n.editedShort],
	LIKED: [ThumbsUpIcon, i18n.liked, i18n.likedShort],
	COMMENTED: [CommentIcon, i18n.commented, i18n.commentedShort],
	SNAPSHOTTED: [EditIcon, i18n.edited, i18n.editedShort],
	STARTED: [EditIcon, i18n.created, i18n.createdShort],
	// these shouldn't be possible for Confluence but are listed for completion
	ASSIGNED: [ClockIcon, i18n.visited, i18n.visitedShort],
	UNASSIGNED: [ClockIcon, i18n.visited, i18n.visitedShort],
	TRANSITIONED: [ClockIcon, i18n.visited, i18n.visitedShort],
};

type RecentListItemProps = {
	recent: RecentActivities | RecentWork;
	index: number;
	onItemClick: (
		index: number,
		action: string,
		status: string | undefined,
		contentId: string,
		spaceKey: string,
		tenantId: string,
		objectType: string,
	) => void;
	onItemSpaceClick: (index: number) => void;
	shouldAutofocus?: boolean;
	useAgenticSessionsAppearance?: boolean;
};

export const RecentListItem: FC<RecentListItemProps> = ({
	recent,
	index,
	onItemClick,
	onItemSpaceClick,
	shouldAutofocus,
	useAgenticSessionsAppearance = false,
}) => {
	const { cloudId } = useSessionData();

	const transformedRecent = useMemo(() => transformActivityData(recent, true), [recent]);

	const content = useMemo(
		() => transformRecentToPageCard(transformedRecent as RecentActivities, '', false, true),
		[transformedRecent],
	);
	const action = transformedRecent.event?.eventType || ActivityEventType.VIEWED;
	const { status, id: contentId, type } = content;
	const spaceKey = content.space.spaceKey;

	const linkRef = useRef<HTMLAnchorElement>(null);

	useEffect(() => {
		if (shouldAutofocus && linkRef.current) {
			linkRef.current.focus();
		}
	}, [shouldAutofocus, linkRef]);

	const CardView = useAgenticSessionsAppearance ? RecentWorkListItemView : NarrowPageCardView;

	return (
		<CardView
			key={index}
			content={content}
			lastActionI18n={ActionIconStrings[action]?.[2] ?? i18n.visitedShort}
			onClick={() => onItemClick(index, action, status, contentId, spaceKey, cloudId, type)}
			onSpaceClick={() => onItemSpaceClick(index)}
		/>
	);
};
